// ADR 8.9.26g §3 — the twenty-six ad-hoc headings, and the one thing they all
// got wrong.
//
// `text-xl font-semibold` on an `<h2>`, `text-lg font-semibold` on an `<h3>`,
// `text-sm font-medium` on an `<h4>`: the visual size was welded to the
// document outline, so a section that needed to sit *inside* another one had to
// choose between the right heading level and the right size. `level` and `size`
// are separate props here for exactly that reason, and `size` defaults to
// `level` so the common case is one prop (AC11).
//
// ADR 28.9.26c §2 — each size is now the Material 3 role Android uses at the
// same kind of site: a page title (`headlineSmall`), a bar title
// (`titleLarge`), a section title such as "I speak" (`titleMedium`) and a
// sheet section header (`titleSmall`).

import type { ReactNode } from 'react'

/** The four levels the tree uses. There is no `<h5>` on any surface. */
export type HeadingLevel = 1 | 2 | 3 | 4

const TAG: Record<HeadingLevel, 'h1' | 'h2' | 'h3' | 'h4'> = { 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4' }

const SIZE: Record<HeadingLevel, string> = {
  1: 'text-headline-sm font-semibold',
  2: 'text-title-lg font-semibold',
  3: 'text-title-md font-semibold',
  4: 'text-title-sm',
}

export interface SectionHeadingProps {
  level?: HeadingLevel
  /** How big it looks, when that is not how deep it sits. */
  size?: HeadingLevel
  /** `ink-muted` for a label-ish heading — `AccountPage`'s section labels. */
  muted?: boolean
  /** ADR 16.9.26t §2 — an in-page anchor's target (the flashcards home's `#browse`). */
  id?: string
  className?: string
  children: ReactNode
}

export function SectionHeading({
  level = 2,
  size,
  muted = false,
  id,
  className = '',
  children,
}: SectionHeadingProps) {
  const Tag = TAG[level]
  const tone = muted ? 'text-ink-muted' : 'text-ink'
  return (
    <Tag id={id} className={`${SIZE[size ?? level]} ${tone} ${className}`}>
      {children}
    </Tag>
  )
}
